import { CommissionLiteModel, useGetCommissions } from 'api-hooks/commission';
import { NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import PaginationComponent from 'components/elements/pagination-component';
import TableComponent, {
  IColumn,
} from 'components/elements/table-component/table-component';
import { format } from 'date-fns';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useNavigation from 'hooks/use-navigation';
import { Container, TableContainer, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { NavigationProps } from 'types';

export type CommissionListParams = null;

export default function CommissionList(
  props: NavigationProps<NavigationRoutes.CommissionList>,
) {
  const { t } = useTranslation();
  const { queryPage } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const navigation = useNavigation();

  const permission = useGetCrudPermission('commission-masters');

  const onClickCreateCommission = React.useCallback(() => {
    if (permission.create) {
      navigation.navigate(NavigationRoutes.CommissionCreate);
    }
  }, [navigation, permission.create]);

  const _columns = React.useMemo<IColumn<CommissionLiteModel>[]>(
    () => [
      {
        header: t('common:department'),
        accessorKey: 'department',
        minSize: 180,
        sortName: 'department',
        cell({ row }) {
          return t(`common:${row.original.department}`);
        },
      },
      {
        header: t('common:applied_on'),
        accessorKey: 'appliedOn',
        sortName: 'applied_on',
        minSize: 180,
        cell: ({ row }) => {
          return row.original.appliedOn
            ? format(row.original.appliedOn, 'dd MMM yyyy')
            : '-';
        },
      },
    ],
    [t],
  );

  const {
    data,
    refetch,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetCommissions,
    {
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const cols = React.useMemo<IColumn<CommissionLiteModel>[]>(() => {
    const meta = data?.meta;
    return [
      {
        header: '#',
        accessorKey: 'index',
        minSize: 40,
        maxSize: 40,
        stickyLeft: true,

        cell: ({ row }) => {
          return (
            <>
              {row.index +
                1 +
                ((meta?.currentPage || 1) - 1) * (meta?.perPage || 1)}
            </>
          );
        },
      },
      ...columns,
    ];
  }, [columns, data?.meta]);

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:commissions')}
          showAdd={permission.create}
          onCreate={onClickCreateCommission}
          onRefresh={refetch}
          linkAdd={NavigationRoutes.CommissionCreate}
        />
        <TopContainer>
          <QueryFilter filters={filters} isUseResponseOptions />
        </TopContainer>
        <TableContainer>
          <TableComponent
            columns={cols}
            isLoading={isFetching}
            data={data?.data || []}
            rowLink={(row) => {
              if (!permission.view) return undefined;
              return {
                pathname: NavigationRoutes.CommissionView,
                query: {
                  id: row.original.id,
                },
              };
            }}
          />
        </TableContainer>
        <PaginationComponent
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
